import * as ReqType from '../types/ServerRequests';
import LoginServices from './LoginServices';
import Axios, { AxiosResponse } from 'axios';
import { AdresseService } from './AdresseService';
import { UserService } from './UserService';
import { ResponsableService } from './ResponsableService';

/**
 * Cette classe permet la communication entre le backend et le frontend
 * Elle assure l'envois la reception des données entre le backend et frontend
 * Elle permet la creation, modification, et suppression des inscriptions
 */
export class SubscriptionsService {
    private static instance: SubscriptionsService = new SubscriptionsService();

    public static getInstance(): SubscriptionsService {
        return this.instance;
    }

    /**
     * creation dela première partie de l'inscription
     * @param {SubscriptionPart1} subscription
     * @param {number} id
     * @returns {AxiosPromise<any>}
     */
    public createSubscribePart1(subscription: ReqType.SubscriptionPart1, id: number) {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Axios.post(
                process.env.REACT_APP_SUBSCRIPTION_STEP_ONE_URL! + '&id=' + id,
                subscription,
                {
                    headers: { 'X-Access-Token': token }
                }
            )
        );
    }

    /**
     * retourne la première partie de l'inscription
     * @param {number} id
     * @returns {Promise<SubscriptionPart1>}
     */
    public getSubscribePart1(id: number): Promise<ReqType.SubscriptionPart1> {
        // const id = LoginServices.getInstance().getUserId();
        if (id !== undefined) {
            return (
                UserService.getInstance().getUser(id).then(
                    (user: ReqType.UserView) => {
                        if (user.userProfile === null) {
                            return this.getDefaultSubscribe1();
                        } else {
                            var retVal: ReqType.SubscriptionPart1 = {
                                adresse: user.userProfile.adresse,
                                date_naissance: user.userProfile.date_naissance,
                                nom: user.userProfile.nom,
                                pointure: user.userProfile.pointure,
                                prenom: user.userProfile.prenom,
                                responsable: user.userProfile.responsable,
                                taille: user.userProfile.taille,
                                telephone: user.userProfile.telephone,
                            };
                            return (retVal);
                        }
                    }
                )
            );
        } else {
            return Promise.reject('');
        }
    }

    /**
     * creation dela deuxième partie de l'inscription
     * @param {SubscriptionPart2} subscription
     * @param {number} id
     * @returns {AxiosPromise<any>}
     */
    public createSubscribePart2(subscription: ReqType.SubscriptionPart2, id: number) {
        const token = LoginServices.getInstance().getUserToken();
        var monInscription = {
            cours: subscription.cours,
            mode_reglement: this.modeToString(subscription.mode_reglement),
            nombre_cheque: subscription.nombre_cheque,
        };
        return (
            Axios.post(
                process.env.REACT_APP_SUBSCRIPTION_STEP_TWO_URL! + '&id=' + id,
                monInscription,
                {
                    headers: { 'X-Access-Token': token }
                }
            )
        );
    }

    /**
     * retourne la deuxième partie de l'inscription
     * @param {number} id
     * @returns {Promise<SubscriptionPart2>}
     */
    public getSubscribePart2(id: number): Promise<ReqType.SubscriptionPart2> {
        // const id = LoginServices.getInstance().getUserId();
        if (id !== undefined) {
            return (
                Promise.all([
                    UserService.getInstance().getUserCourses(id),
                    this.getSubscription(id),
                ]).then(
                    (req) => {
                        const courses: ReqType.Course[] = req[0];
                        const i: ReqType.Subscription = req[1];
                        var retVal: ReqType.SubscriptionPart2 = {
                            cours: [],
                            mode_reglement: i.mode_reglement,
                            nombre_cheque: i.nombre_cheque,
                        };
                        courses.map(
                            (cours: ReqType.Course) => {
                                retVal.cours.push(cours.id);
                            }
                        );
                        return (retVal);
                    }
                )
            );
        } else {
            return Promise.reject('');
        }
    }

    /**
     * creation dela troisième partie de l'inscription
     * @param {SubscriptionPart3} subscription
     * @param {number} id
     * @returns {AxiosPromise<any>}
     */
    public createSubscribePart3(subscription: ReqType.SubscriptionPart3, id: number) {
        const token = LoginServices.getInstance().getUserToken();
        var formData = new FormData();
        formData.append('photo_profil', subscription.photo_profil);
        formData.append('certificat_medical', subscription.certificat_medical);
        formData.append('attestation_assurance', subscription.attestation_assurance);
        formData.append('valide', subscription.valide ? '1' : '0');
        return (
            Axios.post(
                process.env.REACT_APP_SUBSCRIPTION_STEP_THREE_URL! + '&id=' + id,
                formData,
                {
                    headers: { 'X-Access-Token': token }
                }
            )
        );
    }

    /**
     * retourne la troisième partie de l'inscription
     * @returns {Promise<Subscription[]>}
     */
    public getSubscriptions(): Promise<ReqType.Subscription[]> {
        const token = LoginServices.getInstance().getUserToken();
        return (
            Axios.get(
                process.env.REACT_APP_INSCRIPTION_LIST_URL!,
                {
                    headers: { 'X-Access-Token': token },
                }
            ).then(
                (response: AxiosResponse<ReqType.Subscription[]>) => {
                    response.data.map(
                        (sub: ReqType.Subscription) => {
                            sub.etat = this.stringToState(sub.etat.toString());
                            if (sub.mode_reglement === null) {
                                sub.mode_reglement = ReqType.ModeReglement.UNDEFINED;
                            } else {
                                sub.mode_reglement = this.stringToMode(sub.mode_reglement.toString());
                            }
                        }
                    );
                    return response.data;
                }
            )
        );
    }

    /**
     * retourne une inscription à partir d'un id
     * @param {number} id
     * @returns {Promise<Subscription>}
     */
    public getSubscription(id: number): Promise<ReqType.Subscription> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_SUBSCRIPTION_URL! + '&id=' + id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse) => {
                var retVal: ReqType.Subscription = {
                    annee: response.data.annee,
                    etat: this.stringToState(response.data.etat),
                    id: response.data.id,
                    mode_reglement: this.stringToMode(response.data.mode_reglement),
                    nombre_cheque: response.data.nombre_cheque,
                    user: response.data.user,
                };

                return retVal;
            }
        );
    }

    /**
     * retourne la valeur par defaut d'une inscription partie1
     * @returns {SubscriptionPart1}
     */
    public getDefaultSubscribe1(): ReqType.SubscriptionPart1 {
        return {
            adresse: AdresseService.getInstance().getDefaultAdresse(),
            date_naissance: '',
            nom: '',
            prenom: '',
            responsable: ResponsableService.getInstance().getDefaultResponsable(),
            telephone: '',
            pointure: '',
            taille: '',
        };
    }

    /**
     * la validation d'une inscription
     * @param {Subscription} sub
     * @returns {Promise<void>}
     */
    public validateSubscription(sub: ReqType.Subscription): Promise<void> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_SUBSCRIPTION_VALIDATE_URL! + '&id=' + sub.id,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(() => { return; }
        );
    }

    /**
     * retourne la tarif d'une inscription
     * @returns {Promise<Tarif[]>}
     */
    public getTarif(): Promise<ReqType.Tarif[]> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_TARIF_URL!,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Tarif[]>) => {
                return response.data;
            }
        );
    }

    /**
     * la decharge d'une inscription
     * @returns {Promise<Decharges>}
     */
    public getDecharges(): Promise<ReqType.Decharges> {
        const token = LoginServices.getInstance().getUserToken();
        return Axios.get(
            process.env.REACT_APP_DECHARGES_URL!,
            {
                headers: { 'X-Access-Token': token },
            }
        ).then(
            (response: AxiosResponse<ReqType.Decharges>) => {
                return response.data;
            }
        );
    }

    /**
     * retourne l'etat d'une inscription
     * @param {string} s
     * @returns {SubscriptionState}
     */
    private stringToState(s: string): ReqType.SubscriptionState {
        if (s === 'EN COURS') {
            return (ReqType.SubscriptionState.EN_COURS);
        } else if (s === 'A VALIDER') {
            return (ReqType.SubscriptionState.A_VALIDER);
        } else {
            return (ReqType.SubscriptionState.TERMINEE);
        }
    }

    /**
     * retourne le mode de reglement d'une inscription
     * @param {string} s
     * @returns {ModeReglement}
     */
    private stringToMode(s: string): ReqType.ModeReglement {
        if (s === 'ESPECE') {
            return (ReqType.ModeReglement.ESPECES);
        } else {
            return (ReqType.ModeReglement.CHEQUE);
        }
    }

    /**
     * retourne le mode de reglement d'un inscription
     * @param {ModeReglement} s
     * @returns {string}
     */
    private modeToString(s: ReqType.ModeReglement): string {
        if (s === ReqType.ModeReglement.CHEQUE) {
            return ('CHEQUE');
        } else {
            return ('ESPECE');
        }
    }
}


// WEBPACK FOOTER //
// ./src/services/SubscriptionService.tsx